import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { isPowerCapCourse } from "~/common/courses";
import { Download, FileText } from "~/common/redesign-v1/icons";
import { CenteredDialog } from "~/components/ui/centered-dialog";
import { API_PATIENT_IMAGE } from "~/config";
import type { TPatient, TPatientOptionalMedia } from "~/types/patient";

import PatientFiles from "../../patient_files";

/** Same "is this a real generated preview, or just a generic file-type placeholder icon" check
 * `OptionalMedia` below already uses — `media.thumbnail` is either an actual thumbnail image path
 * (a photo that got one) or one of the fixed THUMBNAIL_* marker strings (STL/PDF/ORTHO/MP4, or a
 * photo whose thumbnail generation failed). */
function isRealPhotoThumbnail(media: TPatientOptionalMedia): boolean {
  return !media.thumbnail.match("THUMBNAIL");
}

export function PatientCardUploadedFiles({ patient }: { patient: TPatient }) {
  const { optional_media, plan_media } = patient.media;
  const prefix = API_PATIENT_IMAGE(patient.patient_id, "");

  // Power-cap files use the same read-only row style as the order uploader and exclude scanner integrations.
  if (isPowerCapCourse(patient.course.course_id)) {
    // plan_media's declared type is unknown[] (same assumption OptionalMedia's own plan_media.map
    // below already makes without a cast) — these are actually TPatientOptionalMedia at runtime too.
    const files = [...optional_media, ...(plan_media as TPatientOptionalMedia[])];

    return (
      <div id="extra-media-block" className="form-body" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "1.25rem", width: "100%" }}>
        <h4 className="tw-my-0 tw-text-left tw-text-lg tw-font-medium tw-leading-[27px]">
          <FormattedMessage id="UPLOADED_FILES" />
        </h4>

        {files.length > 0 ? (
          <div className="tw-flex tw-max-w-md tw-flex-col tw-gap-2">
            {files.map((m) => (
              <PowerCapUploadedFileRow key={m.linkto} prefix={prefix} media={m} />
            ))}
          </div>
        ) : (
          <span className="tw-text-sm tw-text-muted-foreground">
            <FormattedMessage id="None" />
          </span>
        )}
      </div>
    );
  }

  return (
    <form id="fileupload extra-media-block" method="post" encType="multipart/form-data" data-upload-template-id="template-upload-2" data-download-template-id="template-download-2">
      <div className="form-body">
        {/* Match the other patient-card section headings and dividers. */}
        <h4 className="tw-mb-5 tw-mt-6 tw-border-t tw-border-[#E5E5E5] tw-pt-3 tw-text-2xl tw-font-normal tw-leading-[1.2] tw-text-foreground">
          <FormattedMessage id="UPLOADED_FILES" />
        </h4>

        {/* The table listing the files available for upload/download */}
        <table role="presentation" className="table table-striped clearfix" style={{ wordBreak: "break-all" }} cellSpacing={0} cellPadding={0} width="100%">
          <tbody className="files">
            {optional_media.map((m) => (
              <OptionalMedia key={m.linkto} prefix={prefix} media={m} />
            ))}

            {plan_media.map((m) => (
              <OptionalMedia key={m.linkto} prefix={prefix} media={m} />
            ))}
          </tbody>
        </table>

        <PatientFiles />
      </div>
    </form>
  );
}

/** Same row markup/classes as PowerCapFileUpload's own "already uploaded" row, minus the remove button. */
function PowerCapUploadedFileRow({ prefix, media }: { prefix: string; media: TPatientOptionalMedia }) {
  const fileName = media.linkto.split("/").slice(-1)[0] ?? media.linkto;

  // Photos open a preview with a download action; other file types download from the row.
  if (isRealPhotoThumbnail(media)) {
    return <PowerCapUploadedPhotoRow prefix={prefix} media={media} fileName={fileName} />;
  }

  return (
    <a
      href={prefix + media.linkto}
      title={fileName}
      download={prefix + media.linkto}
      data-gallery
      data-matomo-mask
      data-hj-suppress
      className="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-[#e7ecf1] tw-bg-white tw-px-3 tw-py-2 tw-no-underline hover:tw-bg-[#fafafa]"
    >
      <FileText className="tw-size-5 tw-shrink-0 tw-text-muted-foreground" aria-hidden />
      <span className="tw-truncate tw-text-sm tw-text-foreground" title={fileName}>
        {fileName}
      </span>
    </a>
  );
}

function PowerCapUploadedPhotoRow({ prefix, media, fileName }: { prefix: string; media: TPatientOptionalMedia; fileName: string }) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const thumbnailSrc = prefix + media.thumbnail;
  const fullSrc = prefix + media.linkto;

  return (
    <>
      <button
        type="button"
        data-matomo-mask
        data-hj-suppress
        onClick={() => setIsPreviewOpen(true)}
        className="tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-border-[#e7ecf1] tw-bg-white tw-px-3 tw-py-2 tw-text-left hover:tw-bg-[#fafafa]"
      >
        <img src={thumbnailSrc} alt="" className="tw-size-8 tw-shrink-0 tw-rounded tw-object-cover" />
        <span className="tw-truncate tw-text-sm tw-text-foreground" title={fileName}>
          {fileName}
        </span>
      </button>

      <CenteredDialog isOpen={isPreviewOpen} onOpenChange={setIsPreviewOpen} closeProps={{}}>
        <div className="tw-flex tw-max-h-[80vh] tw-flex-col tw-items-center tw-gap-4">
          <img src={fullSrc} alt={fileName} className="tw-max-h-[70vh] tw-max-w-full tw-rounded-lg tw-object-contain" />

          <a
            href={fullSrc}
            download={fullSrc}
            data-matomo-mask
            data-hj-suppress
            className="tw-flex tw-items-center tw-gap-2 tw-truncate tw-text-sm tw-text-foreground tw-no-underline hover:tw-underline"
            title={fileName}
          >
            <Download className="tw-size-4 tw-shrink-0" aria-hidden />
            {fileName}
          </a>
        </div>
      </CenteredDialog>
    </>
  );
}

function OptionalMedia({ prefix, media }: { prefix: string; media: TPatientOptionalMedia }) {
  const fileName = media.linkto.split("/").slice(-1)[0];

  if (media.thumbnail.match("THUMBNAIL")) {
    return (
      <tr className="template-download fade in">
        <td style={{ width: "40%" }}>
          <div>
            <a href={prefix + media.linkto} title={media.linkto} download={prefix + media.linkto} data-gallery data-matomo-mask data-hj-suppress>
              <img src={"/img/upload_placeholders/" + media.thumbnail + ".svg" + `?${Number(new Date())}`} style={{ width: "60%" }} alt="" />
            </a>
          </div>
        </td>

        <td>
          <p className="name">
            <a href={prefix + encodeURIComponent(media.linkto)} title={fileName} download={prefix + encodeURIComponent(media.linkto)} data-gallery data-matomo-mask data-hj-suppress>
              {fileName}
            </a>
          </p>
        </td>

        <td>
          <span className="size">{media.media_size} kB</span>
        </td>
      </tr>
    );
  } else {
    return (
      <tr className="template-download fade in">
        <td>
          <span className="preview">
            <a href={prefix + media.linkto} title={fileName} download={prefix + media.linkto} data-gallery data-matomo-mask data-hj-suppress>
              <img src={prefix + media.thumbnail + `?${Number(new Date())}`} alt="" className="tw-size-8 tw-shrink-0 tw-rounded tw-object-cover" />
            </a>
          </span>
        </td>

        <td>
          <p className="name">
            <a href={prefix + media.linkto} title={fileName} download={prefix + media.linkto} data-gallery data-matomo-mask data-hj-suppress>
              {fileName}
            </a>
          </p>
        </td>

        <td>
          <span className="size">{media.media_size} kB</span>
        </td>
      </tr>
    );
  }
}
